import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { classNames } from '@/lib/format';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement>, FieldProps {
  /** Static text rendered inside the box, immediately after the typed value
   *  (e.g. "%", "USDC"). Hidden when the input is empty. Default: undefined. */
  suffix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, id, className, suffix, ...rest },
  forwardedRef,
) {
  const inputId = id ?? rest.name;
  const innerRef = useRef<HTMLInputElement | null>(null);
  // Lets the parent still pass its own ref while we keep our own.
  useImperativeHandle(forwardedRef, () => innerRef.current as HTMLInputElement, []);

  const inputEl = (
    <input ref={innerRef} id={inputId} className={classNames('input', className)} {...rest} />
  );

  if (!suffix) {
    return (
      <label className="field" htmlFor={inputId}>
        {label && <span className="field__label">{label}</span>}
        {inputEl}
        {error ? (
          <span className="field__error">{error}</span>
        ) : hint ? (
          <span className="field__hint">{hint}</span>
        ) : null}
      </label>
    );
  }

  return (
    <label className="field" htmlFor={inputId}>
      {label && <span className="field__label">{label}</span>}
      <InputWithSuffix inputEl={inputEl} value={rest.value} suffix={suffix} />
      {error ? (
        <span className="field__error">{error}</span>
      ) : hint ? (
        <span className="field__hint">{hint}</span>
      ) : null}
    </label>
  );
});

function InputWithSuffix({
  inputEl,
  value,
  suffix,
}: {
  inputEl: ReactNode;
  value: unknown;
  suffix: ReactNode;
}) {
  const [textWidth, setTextWidth] = useState(0);
  const mirrorRef = useRef<HTMLSpanElement | null>(null);

  // Re-measure whenever the value or font might have changed. Reading from
  // mirrorRef.offsetWidth gives pixel-accurate text width using the same
  // font, letter-spacing, etc. as the real input (we copy them in CSS).
  useLayoutEffect(() => {
    if (mirrorRef.current) setTextWidth(mirrorRef.current.offsetWidth);
  }, [value, suffix]);

  // Empty inputs: don't show suffix (it'd float at the left edge).
  const showSuffix = value !== '' && value !== undefined && value !== null;

  return (
    <span className="input__with-suffix">
      {inputEl}
      <span ref={mirrorRef} className="input__mirror" aria-hidden>
        {String(value ?? '')}
      </span>
      {showSuffix && (
        <span
          className="input__suffix-floating"
          aria-hidden
          style={{ left: `calc(var(--input-padding-x, 12px) + ${textWidth}px + 2px)` }}
        >
          {suffix}
        </span>
      )}
    </span>
  );
}
